import React from 'react';
import ExternalLink from '~/components/ExternalLink';
import { Button } from 'reactstrap';

import { getAppContext } from '~/lib/AppContext';
import { AsyncStore } from '~/lib/store/AsyncStore';
import { PagedItem } from '~/lib/helper/PagingHelper';
import { Server } from '~/lib/ApiModel';
import { LoginRoute, ServicesRoute, serverRoute } from '~/lib/helper/RouteHelper';
import Loader from '~/components/Loader';
import PreLoginPanel from '~/components/PreLoginPanel';
import WaitFor from '~/components/WaitFor';
import './Home.scss';

export default class HomePage extends React.Component {
    // Rather than use the standard `servers` store we build our own that loads
    // one server at most and rely on the `total` property to determine if the
    // loaded server is the only server the customer has.
    store = new AsyncStore<PagedItem<Server>, undefined>(
        () => getAppContext().apiClient.getServers({ page: 1, perPage: 1 }),
        undefined
    );

    componentDidMount() {
        // store.load() re-throws on failure, but the error is already surfaced via the
        // tracker status -> WaitFor's `errored` branch, so the rejection is redundant.
        // Handle it here rather than leave it unhandled: Home.test.tsx exercises the
        // failing-load path and Vitest fails the run on unhandled rejections (in
        // production an unhandled rejection would only be a dev-only console warning).
        void this.loadData().catch(() => {});
    }

    loadData = async () => {
        await this.store.load();
        this.maybeRedirect();
    };

    goToLogin = () => {
        getAppContext().navigate(LoginRoute);
    };

    // Cross-SPA: `/home` and `/server/<name>` / `/services` are served by separate PanelPage
    // bundles with different React Router basenames, so <Navigate to="/services"> would resolve
    // against this bundle's basename and land at `/home/services`. Route through
    // getAppContext().navigate() instead, and dispatch from a lifecycle hook (not render) to
    // avoid render-time side effects under StrictMode / multi-render. See !3531 for the pattern.
    private maybeRedirect() {
        const { item } = this.store;
        if (!item) return;
        const url = item.total === 1 && item.items[0] ? serverRoute(item.items[0]) : ServicesRoute;
        getAppContext().navigate(url, true);
    }

    renderLoading() {
        return (
            <PreLoginPanel>
                <Loader text="Loading account..." />
            </PreLoginPanel>
        );
    }

    renderError() {
        const statusUrl = getAppContext().config.statusSite;
        return (
            <PreLoginPanel>
                <div className="homeError" role="alert">
                    <i className="ion-md-warning warningIcon" aria-hidden="true" />
                    <h2>Something went wrong</h2>
                    <p>The service is currently unavailable.</p>
                    <Button color="primary" size="lg" className="w-100" onClick={this.loadData}>
                        Try again
                    </Button>
                    <Button color="link" className="goToLogin" onClick={this.goToLogin}>
                        Go to login
                    </Button>
                    <p className="statusHint">
                        If the problem persists, check the <ExternalLink href={statusUrl}>status page</ExternalLink> for
                        more information.
                    </p>
                </div>
            </PreLoginPanel>
        );
    }

    render() {
        return (
            <WaitFor operation={this.store} loading={this.renderLoading()} errored={this.renderError()}>
                {() => <p>Redirecting...</p>}
            </WaitFor>
        );
    }
}
